<template>
  <div class="doc-strip" :class="`doc-strip--${tone}`">
    <div class="doc-strip-head">
      <v-icon
        :color="iconColor"
        size="14"
        :class="{ 'doc-strip-icon--spin': spinning }"
      >{{ statusIcon }}</v-icon>
      <span class="doc-strip-name">
        {{ progress.chain_name || `Request #${progress.doc_creation_request_id}` }}
      </span>
      <span class="doc-strip-status" :class="`doc-strip-status--${tone}`">
        {{ statusLabel }}
      </span>
      <span
        v-if="progress.progress.total > 0"
        class="doc-strip-count"
      >
        {{ progress.progress.complete }}/{{ progress.progress.total }}
      </span>
    </div>

    <div
      v-if="progress.progress.total > 0 || !isTerminal"
      class="doc-strip-bar"
    >
      <div
        v-if="progress.progress.total > 0"
        class="doc-strip-bar-fill"
        :class="`doc-strip-bar-fill--${tone}`"
        :style="{ width: `${progress.progress.percentage || (isTerminal ? 100 : 0)}%` }"
      />
      <div v-else class="doc-strip-bar-indet" />
    </div>

    <!-- Created document reference chips — open in new tab per Q5.
         rm-portal's in-app routing is legacy (full reload required for
         deep links), so we always open a new tab to keep the chat
         context intact. -->
    <div
      v-if="isTerminal && documents.length > 0"
      class="doc-strip-refs"
    >
      <a
        v-for="doc in documents"
        :key="doc.doc_id || doc.reference"
        :href="docHref(doc)"
        target="_blank"
        rel="noopener"
        class="doc-strip-ref"
      >
        <v-icon size="12" color="green darken-2">mdi-open-in-new</v-icon>
        <span>{{ doc.reference || doc.doc_name || `Doc ${doc.doc_id}` }}</span>
      </a>
    </div>

    <div v-if="isTerminal" class="doc-strip-actions">
      <button
        v-if="progress.narrationMessageId"
        type="button"
        class="doc-strip-btn doc-strip-btn--primary"
        @click="$emit('jump-to', progress.narrationMessageId)"
      >
        <v-icon size="12" color="green darken-2">mdi-arrow-down</v-icon>
        View summary
      </button>
      <button
        type="button"
        class="doc-strip-btn doc-strip-btn--muted"
        title="Dismiss"
        @click="$emit('dismiss', progress.doc_creation_request_id)"
      >
        Dismiss
      </button>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'DocCreationProgressStrip',
  props: {
    progress: { type: Object, required: true },
  },
  emits: ['jump-to', 'dismiss'],
  setup(props) {
    const documents = computed(() => props.progress.documents || []);

    const isTerminal = computed(() =>
      ['Complete', 'Error', 'Failed', 'Timed Out'].includes(props.progress.status),
    );

    const tone = computed(() => {
      switch (props.progress.status) {
        case 'Complete': return 'done';
        case 'Error':
        case 'Failed': return 'error';
        case 'Timed Out': return 'muted';
        default: return 'running';
      }
    });

    const statusIcon = computed(() => {
      switch (props.progress.status) {
        case 'Complete': return 'mdi-check-circle';
        case 'Error':
        case 'Failed': return 'mdi-alert-circle';
        case 'Timed Out': return 'mdi-clock-outline';
        default: return 'mdi-sync';
      }
    });

    const iconColor = computed(() => {
      switch (tone.value) {
        case 'done': return 'green darken-2';
        case 'error': return 'red darken-1';
        case 'muted': return 'grey darken-1';
        default: return 'green darken-1';
      }
    });

    const spinning = computed(
      () => !isTerminal.value,
    );

    const statusLabel = computed(() => {
      switch (props.progress.status) {
        case 'Queued': return 'Queued';
        case 'Processing': return 'Creating';
        case 'Extracting': return 'Extracting';
        case 'Complete': return 'Created';
        case 'Error':
        case 'Failed': return 'Failed';
        case 'Timed Out': return 'Timed out';
        default: return props.progress.status;
      }
    });

    /**
     * Legacy portal routing uses hash-based deep links. Document view
     * routes aren't consistent across all tenants; best-effort build
     * the most common path and open in a new tab so the chat tab is
     * not disrupted.
     */
    function docHref(doc) {
      if (doc.doc_id) return `/documents/${doc.doc_id}`;
      if (doc.reference) return `/documents/ref/${encodeURIComponent(doc.reference)}`;
      return '#';
    }

    return { tone, statusIcon, iconColor, spinning, statusLabel, isTerminal, documents, docHref };
  },
});
</script>

<style scoped>
.doc-strip {
  border-radius: 10px;
  border: 1px solid rgb(167 243 208);
  padding: 6px 10px;
  font-size: 12px;
  background: rgb(236 253 245);
}
.doc-strip--running { background: rgb(236 253 245); border-color: rgb(167 243 208); }
.doc-strip--done    { background: rgb(209 250 229); border-color: rgb(110 231 183); }
.doc-strip--error   { background: rgb(254 242 242); border-color: rgb(252 165 165); }
.doc-strip--muted   { background: rgb(249 250 251); border-color: rgb(229 231 235); }

.doc-strip-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.doc-strip-name {
  font-weight: 600;
  color: rgb(31 41 55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.doc-strip-status {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  flex-shrink: 0;
}
.doc-strip-status--running { color: rgb(4 120 87); }
.doc-strip-status--done    { color: rgb(6 95 70); }
.doc-strip-status--error   { color: rgb(185 28 28); }
.doc-strip-status--muted   { color: rgb(75 85 99); }

.doc-strip-count {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 10px;
  color: rgb(107 114 128);
}

.doc-strip-bar {
  margin-top: 6px;
  height: 4px;
  border-radius: 9999px;
  background: rgb(229 231 235);
  overflow: hidden;
}
.doc-strip-bar-fill {
  height: 100%;
  transition: width 0.2s ease;
}
.doc-strip-bar-fill--running { background: rgb(16 185 129); }
.doc-strip-bar-fill--done    { background: rgb(5 150 105); }
.doc-strip-bar-fill--error   { background: rgb(239 68 68); }
.doc-strip-bar-fill--muted   { background: rgb(156 163 175); }

.doc-strip-bar-indet {
  height: 100%;
  width: 100%;
  background: rgb(110 231 183);
  animation: doc-strip-pulse 1.1s ease-in-out infinite;
}
@keyframes doc-strip-pulse {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}

.doc-strip-icon--spin {
  animation: doc-strip-spin 1.1s linear infinite;
}
@keyframes doc-strip-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.doc-strip-refs {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.doc-strip-ref {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: white;
  border: 1px solid rgb(110 231 183);
  color: rgb(6 95 70);
  text-decoration: none;
  transition: background 0.15s;
}
.doc-strip-ref:hover {
  background: rgb(236 253 245);
}

.doc-strip-actions {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.doc-strip-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.doc-strip-btn--primary {
  background: white;
  border: 1px solid rgb(110 231 183);
  color: rgb(6 95 70);
}
.doc-strip-btn--primary:hover {
  background: rgb(236 253 245);
}
.doc-strip-btn--muted {
  margin-left: auto;
  color: rgb(107 114 128);
}
.doc-strip-btn--muted:hover {
  color: rgb(31 41 55);
}
</style>
